import type { TowerDesign } from "@/game/towerSpec";
import type { StackSection } from "@/game/towerSpec";

/**
 * Westport 180 ft self-support tower (#32, Post Rd East, Westport CT / Verizon),
 * from the stamped tnxTower structural analysis in reference_towers/
 * 02_engineering_drawings/CT_Westport_PostRdEast_lattice_180ft_selfsupport_structural.pdf.
 *
 * EXACT data (report text layer):
 *   - 180 ft (54.86 m), THREE-LEGGED lattice self-support, 13 sections (T1-T13),
 *     each 1 panel. Pipe legs, angle diagonals, bolted flange joints.
 *   - Face width EXACT per section (ft at the top of each section):
 *       0→27.68 (base), 20→25.18, 30→23.93, 40→22.68, 60→20.18, 80→17.68,
 *       90→16.36, 100→15.04, 120→12.79, 126.67→12.10, 133.33→11.40, 140→10.71,
 *       160→8.63, 180→8.54 (top). Triangular circumradius = face·0.3048/√3.
 *   - Diagonals (angles): L3½×3½×¼, L3×3×3⁄16, L2½×2½×3⁄16, L2×2×⅛.
 *     Grades present: A572-50 & A572-42 (legs), A36 (bracing). Exact pipe leg
 *     sizes not tabulated in the extract → mapped to the nearest catalog pipe.
 *   - FOUNDATION (EXACT): three (3) 4.5-ft diameter × 27-ft deep reinforced
 *     concrete drilled piers, one per leg.
 *   - Design: TIA-222-H, Exposure C, 130 mph (Risk Cat III); also 50 mph + ice.
 *     Governing: Diagonal T12 (20-30 ft) at 94.5%.
 */

const R = (ft: number) => (ft * 0.3048) / Math.sqrt(3); // face width (ft) → triangular circumradius (m)
const M = (ft: number) => ft * 0.3048;

// [section top height (ft), face width at top (ft), leg tier, diagonal tier]
const rows: Array<[number, number, "legLight" | "legMed" | "legHeavy" | "legXL", "braceLight" | "braceMed" | "braceHeavy"]> = [
  [20, 25.18, "legXL", "braceHeavy"],
  [30, 23.93, "legXL", "braceHeavy"],
  [40, 22.68, "legXL", "braceHeavy"],
  [60, 20.18, "legHeavy", "braceHeavy"],
  [80, 17.68, "legHeavy", "braceMed"],
  [90, 16.36, "legHeavy", "braceMed"],
  [100, 15.04, "legMed", "braceMed"],
  [120, 12.79, "legMed", "braceMed"],
  [126.67, 12.1, "legMed", "braceLight"],
  [133.33, 11.4, "legMed", "braceLight"],
  [140, 10.71, "legMed", "braceLight"],
  [160, 8.63, "legLight", "braceLight"],
  [180, 8.54, "legLight", "braceLight"],
];

const sections: StackSection[] = rows.map(([ft, face, leg, diag]) => ({
  toHeight: M(ft),
  topRadius: R(face),
  panels: 1,
  legSection: leg,
  braceSection: diag,
  legMaterial: "a572", // legs A572 (Gr50/42)
  braceMaterial: "a36", // angle bracing A36
  bracing: "X",
}));

export const design: TowerDesign = {
  id: "selfsupport-westport-180ft",
  name: "Self-Support (Westport 180 ft)",
  description: "Real 180 ft triangular self-support lattice, Westport CT (Verizon, TIA-222-H 130 mph).",
  author: "reference: CT Westport Post Rd East 180 ft tnxTower SA",
  spec: {
    type: "lattice",
    height: 54.86, // 180 ft
    segments: {
      faces: 3, // three-legged
      baseRadius: R(27.68), // 27.68 ft base face → 4.87 m circumradius
      sections,
    },
    foundation: {
      soil: "dense-sand",
      // EXACT: three 4.5 ft dia × 27 ft deep reinforced concrete drilled piers
      base: { type: "pier", width: 1.372, diameter: 1.372, depth: 8.23, concrete: "c30" },
      connection: {
        kind: "anchorBolts", nBolts: 8, bolt: "b1_5", grade: "f1554_55",
        boltCircleDia: 0.7, embedment: 0.8, edgeDistance: 0.35,
        platePlanArea: 0.5, plateThickness: 0.045, plateGrade: "a36rod", fc: 30e6,
      },
    },
  },
};
